import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { nextgenProjectChatsPath } from "~/pages/manifold-nextgen/paths";
import { createProject, mutateProjectList, projectRouteId } from "~/services/projects";

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewProjectDialog = ({ open, onOpenChange }: NewProjectDialogProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
    }
  }, [open]);

  const { execute, isPending } = useAsyncMutation(createProject, {
    errorMessage: "Failed to create project",
    onSuccess: async (created) => {
      await mutateProjectList();
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.PROJECT_CREATED, {
        projectId: projectRouteId(created),
      });
      onOpenChange(false);
      navigate(nextgenProjectChatsPath(projectRouteId(created)));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void execute({ name: name.trim(), description: description.trim() || null });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New Project"
      description="Group conversations, agents, and datasets under one project."
      contentClassName="sm:max-w-[480px]"
      onSubmit={handleSubmit}
      isPending={isPending}
      primaryLabel="Create Project"
      primaryPendingLabel="Creating…"
      primaryDisabled={!name.trim()}
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="project-name" className="gap-0">
            Name
            <span className="text-destructive" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id="project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Phase II ALS readouts"
            maxLength={500}
            required
            autoFocus
            data-1p-ignore
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="project-description">Description</Label>
          <Textarea
            id="project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this project is for. Markdown supported."
            rows={4}
          />
        </div>
      </div>
    </FormDialog>
  );
};
